import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { utcToZonedTime } from 'date-fns-tz';

const BEIJING_TIME_ZONE = 'Asia/Shanghai';
const normalizedDate = time => {
  if (time instanceof Date) return time;

  if (typeof time === 'string' && !/^-?\d+(\.\d+)?$/.test(time.trim())) {
    return new Date(time);
  }

  const numericTime = Number(time);
  return Math.abs(numericTime) >= 100000000000
    ? new Date(numericTime)
    : fromUnixTime(numericTime);
};

export const beijingTime = time =>
  utcToZonedTime(normalizedDate(time), BEIJING_TIME_ZONE);
const formatBeijingTime = (time, dateFormat) =>
  format(beijingTime(time), dateFormat, { locale: zhCN });

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='HH:mm'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'HH:mm') =>
  formatBeijingTime(time, dateFormat);

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='yyyy年M月d日'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'yyyy年M月d日') => {
  const messageTime = beijingTime(time);
  const now = utcToZonedTime(new Date(), BEIJING_TIME_ZONE);
  const messageDate = format(messageTime, dateFormat, { locale: zhCN });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'yyyy年M月d日 HH:mm', { locale: zhCN });
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = beijingTime(time);
  const now = utcToZonedTime(new Date(), BEIJING_TIME_ZONE);
  const dayDifference = differenceInDays(
    new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    new Date(date.getFullYear(), date.getMonth(), date.getDate())
  );
  if (dayDifference === 0) return format(date, 'HH:mm', { locale: zhCN });
  if (dayDifference === 1) return yesterdayLabel;
  if (isSameYear(date, now)) return format(date, 'M月d日', { locale: zhCN });
  return format(date, 'yyyy年M月d日', { locale: zhCN });
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  return formatDistanceToNow(normalizedDate(time), {
    addSuffix: true,
    locale: zhCN,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='yyyy年M月d日'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'yyyy年M月d日') =>
  formatBeijingTime(time, df);

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  const chineseTime = time.match(
    /(?:不到|大约|约|超过|将近)?\s*(\d+)\s*(秒钟?|分钟|小时|天|个月|年)(?:以内|内|前)?/
  );
  if (chineseTime) {
    const [, value, unit] = chineseTime;
    if ((unit === '秒' || unit === '秒钟' || unit === '分钟') && value === '1') {
      return '刚刚';
    }

    const shortUnit = {
      秒: '秒',
      秒钟: '秒',
      分钟: '分钟',
      小时: '小时',
      天: '天',
      个月: '个月',
      年: '年',
    }[unit];
    return `${value}${shortUnit}${withAgo ? '前' : ''}`;
  }

  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
